import { resolveMessageText } from 'utils/notifications/toast.utils';

export const MAX_RECORD_DURATION_MS = 60000;

export const getDevicesOfType = (types, devicesList) => {
  if (!types) return;

  const deviceTypes = Array.isArray(types) ? types : [types];
  return devicesList.filter((device) => deviceTypes.includes(device.device_type));
};

export const getDeviceOptions = (connectedDevices, group) => {
  const deviceList = getDevicesOfType(['Technology Bridge_Siren/Giant Voice'], connectedDevices);

  return deviceList
    .filter((device) => device?.camera)
    .map((device) => ({
      label: device.alias,
      name: device.alias,
      value: device.id,
      systemType: device.system_type || 'Unknown System',
      deviceType: device.device_type || 'Unknown Device Type',
      forceSelect: group,
    }));
};

export const getRelevantAudioDevices = (connectedDevices, deviceIds) => {
  const scopedIds = new Set((deviceIds || []).map(String));
  return (connectedDevices || [])
    .filter((device) => scopedIds.has(String(device.id)))
    .map((device) => ({ id: device.id, device_type: device.device_type }));
};

export const getScopedDeviceOptions = (deviceOptions, deviceIds) => {
  if (!Array.isArray(deviceIds) || deviceIds.length === 0) return deviceOptions;

  const scopedIds = new Set(deviceIds.map(String));
  const scopedOptions = deviceOptions.filter((option) => scopedIds.has(String(option.value)));
  return scopedOptions.length > 0 ? scopedOptions : deviceOptions;
};

export const filterOptions = (options, searchValue) => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  if (!normalizedSearch) return options;

  return options.filter((option) =>
    `${option.label || ''} ${option.value || ''} ${option.systemType || ''} ${option.deviceType || ''}`
      .toLowerCase()
      .includes(normalizedSearch),
  );
};

export const groupOptionsBySystemAndType = (options, getSelectedCount) => {
  const groups = new Map();

  options.forEach((option) => {
    const systemKey = option.systemType || 'Unknown System';
    const typeKey = option.deviceType || 'Unknown Device Type';
    if (!groups.has(systemKey)) groups.set(systemKey, new Map());
    const systemGroup = groups.get(systemKey);
    if (!systemGroup.has(typeKey)) systemGroup.set(typeKey, []);
    systemGroup.get(typeKey).push(option);
  });

  return Array.from(groups.entries()).map(([systemType, deviceTypes]) => ({
    id: systemType,
    label: systemType,
    sections: Array.from(deviceTypes.entries()).map(([deviceType, items]) => ({
      id: `${systemType}::${deviceType}`,
      label: deviceType,
      items,
      selectedCount: getSelectedCount(items),
    })),
  }));
};

export const getCurrentAssociatedAudioIdsForDevice = (audioList, deviceId) => {
  return audioList
    .filter(
      (audio) =>
        Array.isArray(audio.device_ids) && audio.device_ids.some((id) => `${id}` === `${deviceId}`),
    )
    .map((audio) => audio.id);
};

export const getExpandedAssociationSectionIds = (group) =>
  (group?.sections || []).map((section) => section.id);

export const getInitialAssociatedAudio = (audioList, deviceIds) => {
  const scopedIds = new Set((deviceIds || []).map(String));
  return audioList.find((audio) =>
    (audio.device_ids || []).some((id) => scopedIds.has(String(id))),
  );
};

export const getToggledAssociationChanges = (state, audioList, deviceId, audioId) => {
  const deviceKey = String(deviceId);
  const savedAudio = audioList.find((audio) => audio.id === audioId);
  if (!savedAudio || savedAudio.readonly) return state;

  const isSaved = savedAudio.device_ids.includes(deviceKey);
  const audioChanges = { ...state.draftChanges[audioId] };
  const isAssociated = !(audioChanges[deviceKey] ?? isSaved);
  if (isAssociated === isSaved) delete audioChanges[deviceKey];
  else audioChanges[deviceKey] = isAssociated;

  const draftChanges = { ...state.draftChanges };
  if (Object.keys(audioChanges).length) draftChanges[audioId] = audioChanges;
  else delete draftChanges[audioId];
  return { ...state, draftChanges };
};

export const getStagedAssociationChanges = (state, updates) => {
  const draftChanges = { ...state.draftChanges };
  updates.forEach(({ audioId, addedDeviceIds, removedDeviceIds }) => {
    const audioChanges = { ...draftChanges[audioId] };
    addedDeviceIds.forEach((id) => {
      audioChanges[id] = true;
    });
    removedDeviceIds.forEach((id) => {
      audioChanges[id] = false;
    });
    draftChanges[audioId] = audioChanges;
  });
  return { ...state, draftChanges };
};

// Store only edited audio/device pairs. An absent entry preserves the saved association.
export const getAudioAssociations = (audioList, changes) =>
  audioList.map((audio) => {
    const deviceIds = new Set((audio.device_ids || []).map(String));
    Object.entries(changes[audio.id] || {}).forEach(([deviceId, isAssociated]) => {
      if (isAssociated) deviceIds.add(deviceId);
      else deviceIds.delete(deviceId);
    });
    return { ...audio, device_ids: [...deviceIds] };
  });

export const getAssociationUpdatePlan = (
  audioList,
  draftAudioList,
  deviceOptions,
  groupDeviceIds = [],
  selectedAudioId = null,
) =>
  draftAudioList.flatMap((audio) => {
    const savedAudio = audioList.find((entry) => entry.id === audio.id);
    const previousIds = new Set((savedAudio?.device_ids || []).map(String));
    const nextIds = new Set((audio.device_ids || []).map(String));
    if (audio.id === selectedAudioId) groupDeviceIds.forEach((id) => nextIds.add(String(id)));

    const addedDeviceIds = [...nextIds].filter((id) => !previousIds.has(id));
    const removedDeviceIds = [...previousIds].filter((id) => !nextIds.has(id));
    if (!addedDeviceIds.length && !removedDeviceIds.length) return [];

    return [
      {
        audioId: audio.id,
        audioName: audio.displayName || audio.name || `Audio ${audio.id}`,
        deviceIds: [...nextIds],
        addedDeviceIds,
        removedDeviceIds,
        addedDeviceNames: addedDeviceIds.map(
          (id) =>
            deviceOptions.find((option) => String(option.value) === id)?.label || `Device ${id}`,
        ),
        removedDeviceNames: removedDeviceIds.map(
          (id) =>
            deviceOptions.find((option) => String(option.value) === id)?.label || `Device ${id}`,
        ),
      },
    ];
  });

export const applySuccessfulAssociationUpdates = (state, updates) => {
  const draftChanges = { ...state.draftChanges };
  const savedChanges = { ...state.savedChanges };

  updates.forEach(({ audioId, addedDeviceIds, removedDeviceIds }) => {
    const remainingDraft = { ...draftChanges[audioId] };
    const saved = { ...savedChanges[audioId] };
    [...addedDeviceIds, ...removedDeviceIds].forEach((deviceId) => {
      const isAssociated = addedDeviceIds.includes(deviceId);
      saved[deviceId] = isAssociated;
      if (remainingDraft[deviceId] === isAssociated) delete remainingDraft[deviceId];
    });
    if (Object.keys(remainingDraft).length) draftChanges[audioId] = remainingDraft;
    else delete draftChanges[audioId];
    savedChanges[audioId] = saved;
  });

  return { draftChanges, savedChanges };
};

// A mutation can finish before its query refresh. Keep successful edits visible until acknowledged.
export const reconcileAssociationChanges = (state, audioList) => {
  const draftChanges = {};
  const savedChanges = {};
  let changed = false;
  const audioIds = new Set(audioList.map((audio) => String(audio.id)));

  Object.entries(state.draftChanges).forEach(([audioId, changes]) => {
    if (audioIds.has(audioId)) draftChanges[audioId] = changes;
    else changed = true;
  });
  Object.entries(state.savedChanges).forEach(([audioId, changes]) => {
    const audio = audioList.find((entry) => String(entry.id) === audioId);
    if (!audio) {
      changed = true;
      return;
    }
    const currentIds = new Set((audio.device_ids || []).map(String));
    const pending = Object.entries(changes).filter(
      ([deviceId, isAssociated]) => currentIds.has(deviceId) !== isAssociated,
    );
    if (pending.length !== Object.keys(changes).length) changed = true;
    if (pending.length) savedChanges[audioId] = Object.fromEntries(pending);
  });

  return changed ? { draftChanges, savedChanges } : state;
};

export const getAssociationUpdateResults = (plan, results) => ({
  successfulUpdates: plan.filter((_, index) => results[index].status === 'fulfilled'),
  failedUpdates: plan.filter((_, index) => results[index].status === 'rejected'),
});

export const getAudioSaveErrorMessage = (error) => {
  const backendMessage =
    resolveMessageText(error?.response?.data) ||
    resolveMessageText(error?.data) ||
    resolveMessageText(error?.message);
  return backendMessage
    ? `Audio Manager save step failed: ${backendMessage}`
    : 'Audio Manager save step failed.';
};
